import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthFacadeService } from './auth-facade.service';

// ---- Message shapes (must match extension/src/protocol.ts) ------------------

const ADVOCAD_EXT_SOURCE = 'advocad-ext';

export interface CaptureResult {
  requestId: string;
  url: string;
  content: string;
  captureSource: string;
  rendered: boolean;
}

export interface CaptureFailure {
  requestId: string;
  url: string;
  error: string;
}

interface InstalledMessage {
  source: typeof ADVOCAD_EXT_SOURCE;
  type: 'installed';
  installed: true;
  version: string;
}

interface CaptureCompleteMessage {
  source: typeof ADVOCAD_EXT_SOURCE;
  type: 'capture-complete';
  requestId: string;
  url: string;
  content: string;
  captureSource: string;
  rendered: boolean;
}

interface CaptureFailedMessage {
  source: typeof ADVOCAD_EXT_SOURCE;
  type: 'capture-failed';
  requestId: string;
  url: string;
  error: string;
}

/** The extension's cached token expired mid-send; re-mint and re-hand it. */
interface RequestTokenMessage {
  source: typeof ADVOCAD_EXT_SOURCE;
  type: 'request-token';
}

type PageBoundMessage =
  | InstalledMessage
  | CaptureCompleteMessage
  | CaptureFailedMessage
  | RequestTokenMessage;

// Minimal typing for the `chrome.runtime` surface we use, so the app compiles
// without @types/chrome (the extension APIs only exist when the extension is
// installed; we always guard access).
interface ChromeRuntimeLike {
  sendMessage: (
    extensionId: string,
    message: unknown,
    callback?: (response: unknown) => void
  ) => void;
  lastError?: { message?: string };
}
declare const chrome: { runtime?: ChromeRuntimeLike } | undefined;

/**
 * Bridge between the Advocad Angular app and the assisted-capture Chrome
 * extension.
 *
 *   (a) DETECT the extension (dataset flag + `installed` heartbeat).
 *   (b) SEND `{ type:'capture', url, requestId }` to the extension — preferring
 *       the direct external channel (`chrome.runtime.sendMessage(EXT_ID, …)`),
 *       falling back to `window.postMessage` relayed by the extension's bridge.
 *   (c) LISTEN for the captured `{ url, content }` result (or a failure) and
 *       surface it as an Observable the UI can react to.
 *
 * The service is transport-only. Turning the captured content into an
 * ActivityDraft is done by the caller via CrawlerApiService.crawlFromContent().
 */
@Injectable({
  providedIn: 'root',
})
export class ExtensionBridgeService {
  private readonly _installed$ = new BehaviorSubject<boolean>(false);
  /** True once the extension has been detected. */
  readonly installed$: Observable<boolean> = this._installed$.asObservable();

  private readonly _captured$ = new Subject<CaptureResult>();
  /** Emits when the extension returns captured page content. */
  readonly captured$: Observable<CaptureResult> = this._captured$.asObservable();

  private readonly _failed$ = new Subject<CaptureFailure>();
  /** Emits when a capture fails. */
  readonly failed$: Observable<CaptureFailure> = this._failed$.asObservable();

  private counter = 0;

  /** Latest identity we've published, re-sent whenever the extension announces
   *  itself so a freshly-opened popup always sees the current account. */
  private lastIdentity: { authenticated: boolean; name?: string; email?: string } = {
    authenticated: false,
  };

  constructor(
    private readonly zone: NgZone,
    private readonly authFacade: AuthFacadeService
  ) {
    this.listen();
    this.detect();
    this.broadcastIdentity();
  }

  /** Synchronous best-effort check via the dataset flag the bridge sets. */
  get isInstalled(): boolean {
    if (this._installed$.value) return true;
    try {
      return document.documentElement.dataset['advocadExt'] === '1';
    } catch {
      return false;
    }
  }

  /**
   * Start an assisted capture. MUST be called from within a user gesture (e.g. a
   * button click) so the browser allows the extension to open the tab.
   * Returns the requestId used to correlate the eventual captured$/failed$ emit.
   */
  requestCapture(url: string): string {
    const requestId = `cap-${Date.now()}-${++this.counter}`;

    const extId = environment.advocadExtensionId;
    const runtime = typeof chrome !== 'undefined' ? chrome?.runtime : undefined;

    // Preferred: direct external message to the extension.
    if (extId && runtime?.sendMessage) {
      try {
        runtime.sendMessage(extId, { type: 'capture', url, requestId }, () => {
          // Access lastError to swallow "receiving end does not exist" noise.
          void runtime.lastError;
        });
        return requestId;
      } catch {
        // fall through to postMessage relay
      }
    }

    // Fallback: postMessage the request; the extension's content-bridge relays it.
    window.postMessage(
      { source: 'advocad-app', type: 'capture', url, requestId },
      window.location.origin
    );
    return requestId;
  }

  /** Ask the extension (via the bridge) to re-announce itself. */
  ping(): void {
    window.postMessage(
      { source: 'advocad-app', type: 'ping' },
      window.location.origin
    );
  }

  /**
   * Tell the extension who is signed in, so its popup can show the account a
   * capture will sync to (and prompt a login when nobody is). Only a display
   * name/email crosses the boundary — never a token; the app stays the sole
   * holder of the Auth0 session. Published on every auth change, and mirrored
   * onto documentElement datasets so the content-bridge can read it even if it
   * missed the live message.
   */
  private broadcastIdentity(): void {
    combineLatest([this.authFacade.isAuthenticated$, this.authFacade.user$]).subscribe(
      ([authenticated, user]) => {
        this.lastIdentity = {
          authenticated: !!authenticated,
          name: (user?.name as string) || undefined,
          email: (user?.email as string) || undefined,
        };
        this.publishIdentity();
      }
    );
  }

  private publishIdentity(): void {
    const id = this.lastIdentity;
    try {
      const el = document.documentElement;
      el.dataset['advocadAuth'] = id.authenticated ? '1' : '0';
      if (id.authenticated && (id.name || id.email)) {
        el.dataset['advocadUser'] = JSON.stringify({ name: id.name, email: id.email });
      } else {
        delete el.dataset['advocadUser'];
      }
    } catch {
      /* SSR / non-DOM context */
    }
    window.postMessage(
      { source: 'advocad-app', type: 'identity', ...id },
      window.location.origin
    );
    this.sendToken();
  }

  /**
   * Hand the user's access token to the extension over the SECURE external
   * channel (chrome.runtime.sendMessage to our whitelisted extension id) so it
   * can post captures directly as the user. Never via postMessage/dataset — a
   * token must not be readable by page scripts. Cleared on sign-out.
   */
  private sendToken(): void {
    const extId = environment.advocadExtensionId;
    const runtime = typeof chrome !== 'undefined' ? chrome?.runtime : undefined;
    if (!extId || !runtime?.sendMessage) return;

    const send = (accessToken: string) => {
      try {
        runtime.sendMessage(
          extId,
          {
            type: 'token',
            accessToken,
            apiBaseUrl: environment.apiBaseUrl,
            // This frontend's own origin, so the popup's "Open Advocad" follows
            // whichever app connected (http://localhost:4200 in dev, prod later).
            appOrigin: window.location.origin,
            // Realm-style data-routing headers the API requires to persist —
            // mirrors DbContextInterceptor so the extension's direct POST works.
            dbContext: {
              db: environment.db.name,
              activityCollection: environment.db.activityCollection,
              userProfileCollection: environment.db.userProfileCollection,
            },
          },
          () => void runtime.lastError
        );
      } catch {
        /* extension not installed / not reachable */
      }
    };

    if (!this.lastIdentity.authenticated) {
      send(''); // clear any cached token in the extension
      return;
    }
    this.authFacade.getAccessToken().subscribe({
      next: (token) => send(token),
      error: () => {},
    });
  }

  private detect(): void {
    if (this.isInstalled) {
      this._installed$.next(true);
    }
    // Nudge the bridge to announce, in case the app loaded after it.
    this.ping();
  }

  private listen(): void {
    window.addEventListener('message', (event: MessageEvent) => {
      if (event.source !== window) return;
      if (event.origin !== window.location.origin) return;
      const data = event.data as PageBoundMessage;
      if (!data || data.source !== ADVOCAD_EXT_SOURCE) return;

      // Re-enter Angular's zone so bound UI updates without manual CD.
      this.zone.run(() => {
        switch (data.type) {
          case 'installed':
            this._installed$.next(true);
            // Re-send the current account so a just-opened popup sees it.
            this.publishIdentity();
            break;
          case 'capture-complete':
            this._captured$.next({
              requestId: data.requestId,
              url: data.url,
              content: data.content,
              captureSource: data.captureSource,
              rendered: data.rendered,
            });
            break;
          case 'capture-failed':
            this._failed$.next({
              requestId: data.requestId,
              url: data.url,
              error: data.error,
            });
            break;
          case 'request-token':
            // The extension's cached token expired mid-send. Re-mint a fresh one
            // (getAccessTokenSilently) and re-hand it over the secure channel so
            // the send can retry without the user reopening the app.
            this.sendToken();
            break;
        }
      });
    });
  }
}
